import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Send } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/common/Pagination';
import { EmptyState } from '@/components/common/EmptyState';
import { RcsDispatchDetailDialog } from './RcsDispatchDetailDialog';
import { useRcsDispatchesList } from '@/hooks/useRcs';
import type { RcsDispatch, RcsDispatchStatus } from '@/types/api.types';

const PAGE_SIZE = 20;

const ALL_STATUSES: RcsDispatchStatus[] = ['pending', 'sent', 'delivered', 'read', 'failed'];

const STATUS_LABEL: Record<RcsDispatchStatus, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
};

const STATUS_VARIANT: Record<
  RcsDispatchStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'outline',
  sent: 'secondary',
  delivered: 'default',
  read: 'default',
  failed: 'destructive',
};

interface Props {
  // Omit to list all RCS dispatches for the tenant (campaigns + avulsos/quick sends).
  campaignId?: string;
}

function DispatchesSkeleton() {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-24" />
        </div>
      ))}
    </div>
  );
}

export function RcsCampaignDispatchesTable({ campaignId }: Props) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<RcsDispatchStatus | undefined>();
  const [selected, setSelected] = useState<RcsDispatch | null>(null);

  const { data, isLoading } = useRcsDispatchesList({
    campaignId,
    status: statusFilter,
    page,
    pageSize: PAGE_SIZE,
  });

  const dispatches = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => {
            setStatusFilter(undefined);
            setPage(1);
          }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
            !statusFilter
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-input text-muted-foreground hover:text-foreground'
          }`}
        >
          Todos
        </button>
        {ALL_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
              statusFilter === s
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-input text-muted-foreground hover:text-foreground'
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <DispatchesSkeleton />
      ) : dispatches.length === 0 ? (
        <EmptyState icon={Send} title="Nenhum disparo encontrado" />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Destinatário</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Enviado em</TableHead>
                <TableHead>Erro</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dispatches.map((d) => (
                <TableRow key={d.id} className="cursor-pointer" onClick={() => setSelected(d)}>
                  <TableCell>
                    <p className="font-mono text-xs">{d.contactPhone}</p>
                    {d.contactName && (
                      <p className="text-xs text-muted-foreground">{d.contactName}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[d.status]}>{STATUS_LABEL[d.status]}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.sentAt
                      ? format(new Date(d.sentAt), "dd/MM 'às' HH:mm", { locale: ptBR })
                      : '—'}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-xs text-destructive">
                    {d.errorMessage ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {meta && (
            <Pagination
              page={page}
              totalPages={meta.totalPages}
              total={meta.total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <RcsDispatchDetailDialog
        dispatch={selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </div>
  );
}
